import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DiningAreasService } from '../dining-areas.service';

export interface DiningAreaFormDialogData {
  id?: string;
}

@Component({
  selector: 'app-dining-area-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>
        <mat-icon>table_restaurant</mat-icon>
        {{ isEditMode ? 'Edit Dining Area' : 'Create Dining Area' }}
      </h2>
      <button mat-icon-button type="button" (click)="onCancel()" [disabled]="loading">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="form" (ngSubmit)="onSubmit()" id="areaForm">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" placeholder="e.g. Ground Floor" required />
          <mat-error>{{ getError('name') }}</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <input matInput formControlName="description" placeholder="Optional description" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Sort order</mat-label>
          <input matInput type="number" formControlName="sort_order" min="0" />
        </mat-form-field>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_active" color="accent">Active</mat-slide-toggle>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button color="primary" form="areaForm" type="submit" [disabled]="loading || form.invalid">
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">{{ isEditMode ? 'Update' : 'Create' }}</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-header { display: flex; align-items: center; justify-content: space-between; }
    .dialog-header h2 { display: flex; align-items: center; gap: 8px; margin: 0; }
    mat-dialog-content { min-width: 520px; padding-top: 8px; }
    .full-width { width: 100%; }
    .form-section { margin: 8px 0; }
    mat-dialog-actions button mat-spinner { display: inline-block; margin-right: 8px; }
    @media (max-width: 600px) { mat-dialog-content { min-width: unset; } }
  `],
})
export class DiningAreaFormDialogComponent {
  form: FormGroup;
  loading = false;
  isEditMode: boolean;
  id?: string;

  constructor(
    private fb: FormBuilder,
    private service: DiningAreasService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<DiningAreaFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DiningAreaFormDialogData,
  ) {
    this.id = data?.id;
    this.isEditMode = !!this.id;
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(500)]],
      sort_order: [0, [Validators.min(0)]],
      is_active: [true],
    });

    if (this.isEditMode) {
      this.load();
    }
  }

  private load(): void {
    if (!this.id) return;
    this.loading = true;
    this.service.get(this.id).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.form.patchValue({
            name: res.data.name,
            description: res.data.description,
            sort_order: res.data.sort_order,
            is_active: res.data.is_active,
          });
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load dining area', 'Close', { duration: 3000 });
        this.loading = false;
        this.dialogRef.close(false);
      },
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const request$ = this.isEditMode && this.id
      ? this.service.update(this.id, this.form.value)
      : this.service.create(this.form.value);

    request$.subscribe({
      next: (res) => {
        if (res.success) {
          this.snackBar.open(`Dining area ${this.isEditMode ? 'updated' : 'created'} successfully`, 'Close', { duration: 3000 });
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: (error) => {
        this.snackBar.open(error.error?.message || 'Failed to save dining area', 'Close', { duration: 5000 });
        if (error.error?.errors) {
          Object.keys(error.error.errors).forEach((key) => {
            this.form.get(key)?.setErrors({ serverError: error.error.errors[key][0] });
          });
        }
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getError(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control?.errors) return '';
    if (control.errors['required']) return 'This field is required';
    if (control.errors['maxlength']) return 'Too long';
    if (control.errors['serverError']) return control.errors['serverError'];
    return 'Invalid value';
  }
}
